<template>
  <div class="content ebgf">
    <form class="form-horizontal" @submit.prevent="onSubmit">
      <!-- Section A: 그룹 정보 -->
      <card class="text-left">
        <template slot="header">
          <h4 class="card-title">배너 그룹 정보</h4>
        </template>

        <!-- 타이틀 -->
        <div class="ebgf-field">
          <label class="ebgf-label">타이틀 <span class="ebgf-required">*</span></label>
          <div class="ebgf-input-stack">
            <div class="ebgf-lang-row">
              <span class="ebgf-lang-code">KO</span>
              <base-input placeholder="한국어 타이틀" v-model="title" />
            </div>
            <div class="ebgf-lang-row">
              <span class="ebgf-lang-code">EN</span>
              <base-input placeholder="영어 타이틀" v-model="titleEn" />
            </div>
            <div class="ebgf-lang-row">
              <span class="ebgf-lang-code">JA</span>
              <base-input placeholder="일본어 타이틀" v-model="titleJa" />
            </div>
          </div>
        </div>

        <hr class="ebgf-sep" />

        <!-- 설정 -->
        <div class="ebgf-settings">
          <div class="ebgf-field ebgf-field--inline">
            <label class="ebgf-label">레이아웃 타입 <span class="ebgf-required">*</span></label>
            <el-select v-model="layoutType" placeholder="선택" class="select-primary">
              <el-option v-for="opt in layoutOptions" :key="opt.value" :value="opt.value" :label="opt.label" class="select-primary" />
            </el-select>
          </div>
          <div class="ebgf-field ebgf-field--inline">
            <label class="ebgf-label">표시 여부</label>
            <el-select v-model="groupState" class="select-primary">
              <el-option :value="1" label="표시" class="select-primary" />
              <el-option :value="0" label="숨김" class="select-primary" />
            </el-select>
          </div>
        </div>
      </card>

      <!-- Section B: 배너 목록 -->
      <card class="text-left">
        <template slot="header">
          <div class="ebgf-banner-header">
            <h4 class="card-title">배너 목록</h4>
            <base-button type="default" size="sm" class="ebgf-banner-add-btn" @click="openBannerModal">배너 추가</base-button>
          </div>
        </template>

        <div v-if="banners.length === 0" class="ebgf-banner-empty">배너를 추가해주세요.</div>

        <div v-for="(banner, index) in banners" :key="index" class="ebgf-banner-item">
          <div class="ebgf-banner-row">
            <span class="ebgf-banner-order">#{{ index + 1 }}</span>

            <div class="ebgf-banner-thumb-slot">
              <div class="ebgf-banner-thumb" :class="'ebgf-banner-thumb--' + (layoutType || 'square').toLowerCase()">
                <img v-if="banner.imageFileUrl" :src="banner.imageFileUrl" :alt="banner.title" />
                <div v-else class="ebgf-banner-thumb-empty"></div>
              </div>
            </div>

            <div class="ebgf-banner-info">
              <div class="ebgf-banner-title-row">
                <span class="badge badge-pill" :class="banner.state === 1 ? 'badge-success' : 'badge-secondary'">
                  {{ banner.state === 1 ? "표시" : "숨김" }}
                </span>
                <span class="ebgf-banner-title">{{ banner.title || "(제목 없음)" }}</span>
              </div>
              <span class="ebgf-banner-period">{{ formatPeriod(banner) }}</span>
              <p v-if="banner.link" class="ebgf-banner-link">{{ banner.link }}</p>
            </div>

            <div class="ebgf-banner-actions">
              <base-button v-if="banner.bannerId" type="info" size="sm" @click.stop="goBannerForm(banner.bannerId)">상세수정</base-button>
              <base-button type="danger" size="sm" @click.stop="removeBanner(index)">삭제</base-button>
            </div>
          </div>

          <hr class="ebgf-divider" />
        </div>
      </card>

      <event-banner-select-modal :visible.sync="showBannerModal" :unit-id="unitId" :layout-type="layoutType" @select="addSelectedBanners" />

      <div class="ebgf-actions">
        <base-button native-type="submit" type="success">{{ groupId ? "수정" : "추가" }}</base-button>
        <base-button type="default" @click="goBack">취소</base-button>
        <base-button v-if="groupId" type="danger" @click="deleteGroup">그룹 삭제</base-button>
      </div>
    </form>
  </div>
</template>

<script>
import { BaseButton, BaseInput, Card } from "@/components/index";
import { formatDateTime } from "@/util/DateUtils";
import { Toast } from "@/util/Toast";
import swal from "sweetalert2";
import EventBannerSelectModal from "./EventBannerSelectModal.vue";

export default {
  components: { Card, BaseButton, BaseInput, EventBannerSelectModal },
  async created() {
    if (this.$route.query.groupId) {
      this.groupId = Number(this.$route.query.groupId);
      await this.getData();
    }
    if (this.$route.query.unitId) {
      this.unitId = Number(this.$route.query.unitId);
    }
  },
  data() {
    return {
      groupId: null,
      unitId: null,
      title: "",
      titleEn: "",
      titleJa: "",
      layoutType: null,
      groupState: 1,
      banners: [],
      showBannerModal: false,
      layoutOptions: [
        { value: "SQUARE", label: "정방형" },
        { value: "HORIZONTAL", label: "가로형" },
        { value: "VERTICAL", label: "세로형" },
      ],
    };
  },
  methods: {
    async getData() {
      const res = await this.$axios.get(`/eventBanner/groups/${this.groupId}`);
      const data = res.data.data;
      this.title = data.title;
      this.titleEn = data.titleEn || "";
      this.titleJa = data.titleJa || "";
      this.layoutType = data.layoutType;
      this.groupState = data.state;
      this.banners = data.banners.map((b) => ({
        ...b,
        imageFileUrl: b.imageFileId ? window.Endpoints.imageServerUrl + b.imageFileId : null,
      }));
    },
    async removeBanner(index) {
      const result = await swal.fire({
        title: "배너 삭제",
        html: "그룹에서 배너를 삭제하시겠습니까?<br>저장/수정을 누르면 최종 반영됩니다.",
        type: "warning",
        showCancelButton: true,
        customClass: {
          confirmButton: "btn btn-danger btn-fill",
          cancelButton: "btn btn-cancel btn-fill",
        },
        confirmButtonText: "삭제",
        cancelButtonText: "취소",
        buttonsStyling: false,
      });
      if (!result.value) return;
      this.banners.splice(index, 1);
    },
    openBannerModal() {
      this.showBannerModal = true;
    },
    addSelectedBanners(selected) {
      const existingIds = new Set(this.banners.map((b) => b.bannerId));
      const toAdd = selected
        .filter((b) => !existingIds.has(b.bannerId))
        .map((b) => ({
          ...b,
          imageFileUrl: b.imageFileId ? window.Endpoints.imageServerUrl + b.imageFileId : null,
        }));
      this.banners.push(...toAdd);
    },
    formatPeriod(banner) {
      if (!banner.startTime && !banner.endTime) return "-";
      const start = banner.startTime ? formatDateTime(banner.startTime) : "?";
      const end = banner.endTime ? formatDateTime(banner.endTime) : "?";
      return `${start} ~ ${end}`;
    },
    validate() {
      if (!this.title.trim()) {
        Toast.error("그룹 타이틀(KO)을 입력해주세요");
        return false;
      }
      if (!this.layoutType) {
        Toast.error("레이아웃 타입을 선택해주세요");
        return false;
      }
      if (this.banners.length === 0) {
        Toast.error("배너를 1개 이상 추가해주세요");
        return false;
      }
      return true;
    },
    buildPayload() {
      return {
        unitId: this.unitId,
        title: this.title,
        titleEn: this.titleEn || null,
        titleJa: this.titleJa || null,
        layoutType: this.layoutType,
        state: this.groupState,
        bannerIds: this.banners.map((b) => b.bannerId).filter(Boolean),
      };
    },
    onSubmit() {
      if (!this.validate()) return;
      const payload = this.buildPayload();
      const handleError = (err) => {
        const message = err.response?.data?.error?.message;
        Toast.error(message || "오류가 발생했습니다");
      };
      if (this.groupId) {
        this.$axios
          .put(`/eventBanner/groups/${this.groupId}`, { params: payload })
          .then(() => {
            Toast.success("수정되었습니다");
            this.goBack();
          })
          .catch(handleError);
      } else {
        if (this.banners.length > 10) {
          Toast.error("등록 가능한 배너 최대 갯수를 초과했습니다");
          return;
        }
        this.$axios
          .post("/eventBanner/groups", { params: payload })
          .then(() => {
            Toast.success("추가되었습니다");
            this.goBack();
          })
          .catch(handleError);
      }
    },
    async deleteGroup() {
      const result = await swal.fire({
        title: "그룹 삭제",
        html: "배너 그룹을 삭제하시겠습니까?",
        type: "warning",
        showCancelButton: true,
        customClass: {
          confirmButton: "btn btn-danger btn-fill",
          cancelButton: "btn btn-cancel btn-fill",
        },
        confirmButtonText: "삭제",
        cancelButtonText: "취소",
        buttonsStyling: false,
      });
      if (!result.value) return;
      this.$axios.delete(`/eventBanner/groups/${this.groupId}`).then(() => {
        Toast.success("삭제되었습니다");
        this.goBack();
      });
    },
    goBannerForm(bannerId) {
      this.$router.push(`/banner/eventBannerForm?bannerId=${bannerId}`);
    },
    goBack() {
      history.go(-1);
    },
  },
};
</script>

<style scoped lang="scss">
.ebgf-actions {
  display: flex;
  gap: 8px;
  margin-top: 8px;
  justify-content: center;
}

.ebgf-required {
  color: #f5365c;
}

/* 그룹 정보 폼 */
.ebgf-field {
  display: flex;
  gap: 20px;
  align-items: flex-start;
  margin-bottom: 0;
}

.ebgf-field--inline {
  align-items: center;
}

.ebgf-label {
  flex-shrink: 0;
  width: 110px;
  padding-top: 10px;
  color: #525f7f;
  font-size: 0.875rem;
  font-weight: 600;
  text-align: right;
}

.ebgf-input-stack {
  flex: 1;
  max-width: 900px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ebgf-lang-row {
  display: flex;
  align-items: center;
  gap: 10px;

  > .form-group {
    flex: 1;
    margin-bottom: 0;
  }
}

.ebgf-lang-code {
  flex-shrink: 0;
  width: 24px;
  color: #8898aa;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.ebgf-sep {
  margin: 16px 0;
  border-color: #e9ecef;
}

.ebgf-settings {
  display: flex;
  gap: 40px;
  flex-wrap: wrap;

  .ebgf-label {
    padding-top: 0;
  }
}

.ebgf-banner-header {
  display: flex;
  gap: 10px;
  align-items: center;
  width: 100%;

  .card-title {
    margin: 0;
  }
}

.ebgf-banner-add-btn {
  margin-left: auto;
}

.ebgf-banner-empty {
  padding: 40px 0;
  color: #8898aa;
  text-align: center;
}

.ebgf-banner-item {
  margin-bottom: 4px;
}

.ebgf-banner-row {
  display: flex;
  gap: 16px;
  align-items: center;
  padding: 10px 0;
}

.ebgf-banner-order {
  flex-shrink: 0;
  width: 28px;
  color: #8898aa;
  font-size: 0.78rem;
  font-weight: 700;
  text-align: right;
}

/* 썸네일 고정 슬롯 — 타입이 달라도 행 높이 일정 */
.ebgf-banner-thumb-slot {
  flex-shrink: 0;
  width: 200px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ebgf-banner-thumb {
  overflow: hidden;
  border-radius: 6px;
  background: #f4f5f7;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &--square {
    width: 120px;
    aspect-ratio: 1 / 1;
  }

  &--horizontal {
    width: 190px;
    aspect-ratio: 95 / 53;
  }

  &--vertical {
    width: 85px;
    aspect-ratio: 380 / 539;
  }
}

.ebgf-banner-thumb-empty {
  width: 100%;
  height: 100%;
  background: #e9ecef;
}

/* 제목 + 기간: 남은 공간 차지, 넘침은 잘라냄 */
.ebgf-banner-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ebgf-banner-title-row {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;

  .badge {
    flex-shrink: 0;
  }
}

.ebgf-banner-title {
  overflow: hidden;
  color: #32325d;
  font-size: 1rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ebgf-banner-period {
  color: #8898aa;
  font-size: 0.85rem;
  white-space: nowrap;
}

.ebgf-banner-link {
  overflow: hidden;
  margin: 1px 0 0;
  color: #8898aa;
  font-size: 0.72rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 배지 + 버튼: 우측 고정 */
.ebgf-banner-actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.ebgf-divider {
  border-color: #e9ecef;
}
</style>
